import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import './Listings.css';

function Listings() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    loadProperties();
  }, []);

  const loadProperties = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('properties')
        .select('*')
        .in('status', ['For Sale', 'Pending Sale', 'Recently Sold'])
        .eq('display', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setProperties(data || []);
    } catch (error) {
      console.error('Error loading properties:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const calculateDaysOnMarket = (listingDate, pendingDate) => {
    if (!listingDate) return 0;

    const startDate = new Date(listingDate + 'T00:00:00');
    const endDate = pendingDate ? new Date(pendingDate + 'T00:00:00') : new Date();

    // Reset hours to start of day for accurate day calculation
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);

    const diffTime = endDate - startDate;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    return diffDays >= 0 ? diffDays : 0;
  };

  const handleMoreInfo = (property) => {
    // Navigate to contact page with property info as state
    navigate('/contact', {
      state: {
        propertyAddress: `${property.address}, ${property.city}, ${property.state} ${property.zip}`,
        propertyPrice: formatPrice(property.price)
      }
    });
  };

  const handleNextImage = (propertyId, imagesLength) => {
    setCurrentImageIndex(prev => ({
      ...prev,
      [propertyId]: ((prev[propertyId] || 0) + 1) % imagesLength
    }));
  };

  const handlePrevImage = (propertyId, imagesLength) => {
    setCurrentImageIndex(prev => ({
      ...prev,
      [propertyId]: ((prev[propertyId] || 0) - 1 + imagesLength) % imagesLength
    }));
  };

  const getPropertyImages = (property) => {
    console.log('Property data:', property);
    console.log('Images field:', property.images);
    console.log('Images length:', property.images?.length);

    if (property.images && property.images.length > 0) {
      return property.images;
    }
    return property.image ? [property.image] : [];
  };

  return (
    <div className="listings-page">
      <div className="listings-header">
        <h1>Featured Properties</h1>
      </div>

      {loading ? (
        <div className="loading-container">
          <p>Loading properties...</p>
        </div>
      ) : properties.length === 0 ? (
        <div className="no-properties-container">
          <p>No properties available at this time. Please check back soon!</p>
        </div>
      ) : (
        <div className="listings-container">
          <div className="listings-grid">
            {properties.map((property) => {
              const images = getPropertyImages(property);
              const currentIndex = currentImageIndex[property.id] || 0;
              const hasMultipleImages = images.length > 1;

              return (
            <div key={property.id} className="property-card">
              <div className="property-image-container">
                {images.length > 0 ? (
                  <>
                    <img
                      src={images[currentIndex]}
                      alt={`${property.address} - Image ${currentIndex + 1}`}
                      className="property-image"
                    />
                    {hasMultipleImages && (
                      <>
                        <button
                          className="carousel-btn prev-btn"
                          onClick={() => handlePrevImage(property.id, images.length)}
                          aria-label="Previous image"
                        >
                          ‹
                        </button>
                        <button
                          className="carousel-btn next-btn"
                          onClick={() => handleNextImage(property.id, images.length)}
                          aria-label="Next image"
                        >
                          ›
                        </button>
                        <div className="carousel-indicators">
                          {images.map((_, idx) => (
                            <span
                              key={idx}
                              className={`indicator ${idx === currentIndex ? 'active' : ''}`}
                              onClick={() => setCurrentImageIndex(prev => ({ ...prev, [property.id]: idx }))}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <div className="no-image">No Image Available</div>
                )}
                <div className={`property-status status-${property.status.toLowerCase().replace(' ', '-')}`}>
                  {property.status}
                </div>
                <div className="days-on-market">
                  {calculateDaysOnMarket(property.listing_date, property.pending_date)} days on market
                </div>
              </div>

              <div className="property-details">
                <div className="property-header-row">
                  <div className="property-address">
                    <div className="address-line">{property.address}</div>
                    <div className="city-state">{property.city}, {property.state} {property.zip}</div>
                  </div>
                  <div className="property-price">{formatPrice(property.price)}</div>
                </div>

                <div className="property-stats">
                  <span className="stat">
                    <strong>{property.beds || 0}</strong> bd
                  </span>
                  <span className="stat-divider">|</span>
                  <span className="stat">
                    <strong>{property.baths || 0}</strong> ba
                  </span>
                  <span className="stat-divider">|</span>
                  <span className="stat">
                    <strong>{property.sqft ? formatNumber(property.sqft) : 0}</strong> sqft
                  </span>
                </div>

                {property.description && (
                  <div className="property-description">
                    {property.description}
                  </div>
                )}

                <div className="property-footer">
                  {property.lot_size && <div className="lot-size">Lot: {formatNumber(property.lot_size)} sqft</div>}
                  {property.status === 'For Sale' && (
                    <button
                      className="more-info-btn"
                      onClick={() => handleMoreInfo(property)}
                    >
                      More Info
                    </button>
                  )}
                </div>
              </div>
            </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="contact-agent-section">
        <div className="contact-agent-content">
          <h2>Interested in these properties?</h2>
          <p>Contact Gita Kashani for more information or to schedule a viewing.</p>
          <div className="contact-buttons">
            <a href="tel:650-283-3537" className="contact-btn phone-btn">
              Call (650) 283-3537
            </a>
            <a href="/contact" className="contact-btn email-btn">
              Send Message
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Listings;
